/**
 * Broad grouping of the audited action — this is what CS filters the admin
 * audit-history timeline by, so every value must stay meaningful to a support rep.
 *
 * This is a CLOSED taxonomy: pick the closest existing value for the DATA BEING
 * TOUCHED (not the feature doing the touching). Do not add a value for a new
 * feature area without team sign-off — each addition permanently widens the
 * admin filter dropdown and fragments the history it exists to unify.
 */
export enum EAuditCategory {
    /** Authentication/authorization events: logins, token issuance. */
    AUTH = "auth",
    /** Mutations of client (borrower) records — the client collection holds their PII. */
    CLIENT_PII = "client_pii",
    /** Report lifecycle: generation, edits, deletion, share links, exports. */
    REPORTS = "reports",
    /** Outbound messages leaving the system on a user's behalf: emails, SMS, Braze sends, invites. */
    COMMS = "comms",
    /** Alert/notification preference changes, incl. SMS/voice consent opt-in/opt-out. */
    NOTIFICATION_PREFS = "notification_prefs",
    /** Mutations of the user's own profile data: branding, co-branding, headshots, preferences. */
    PROFILE_PII = "profile_pii",
    /** User-created content: recordings, morning updates, short URLs, meeting logs, AI feedback. */
    CONTENT = "content",
}

/**
 * The specific action that occurred, always paired with an {@link EAuditCategory}.
 * Same rule as the category: CLOSED taxonomy, closest fit wins, additions need
 * team sign-off. Prefer the generic CRUD verbs; the specific verbs below exist
 * only where CRUD would hide meaning CS needs (e.g. SEND vs CREATE for an email).
 */
export enum EAuditAction {
    /** A resource was persisted for the first time. */
    CREATE = "create",
    /** An existing resource was modified. */
    UPDATE = "update",
    /** A resource was removed. */
    DELETE = "delete",
    /** A derived artifact was produced (e.g. report generation) — not for plain CREATE. */
    GENERATE = "generate",
    /** Share access was created (live-share links, report share URLs). */
    SHARE = "share",
    /** Data left the system as a file/download (e.g. report export) — data egress. */
    EXPORT = "export",
    /** An outbound message was dispatched (email, SMS, Braze). Pairs with COMMS. */
    SEND = "send",
    /** A session was established. Pairs with AUTH. */
    LOGIN = "login",
    /** A token/JWT was issued. Pairs with AUTH. */
    TOKEN_GENERATE = "token_generate",
    /** Opted in to a notification channel (incl. SMS/voice consent). Pairs with NOTIFICATION_PREFS. */
    SUBSCRIBE = "subscribe",
    /** Opted out of a notification channel (incl. SMS/voice consent). Pairs with NOTIFICATION_PREFS. */
    UNSUBSCRIBE = "unsubscribe",
}

/**
 * One entry in the audit_log collection. Records THAT an action happened —
 * who, what kind, on which resource — never WHAT the values were. No
 * before/after diffs, no field contents. Written on the success path only
 * (never log an action whose underlying operation threw).
 */
export interface IAuditLog {
    _id?: string;
    /** User who performed the action (omitted for system/unauthenticated events). */
    actorUserId?: string;
    /** User the action was performed on or for. */
    targetUserId?: string;
    action: EAuditAction;
    category: EAuditCategory;
    /** Type of resource affected, e.g. "client", "report", "branding". */
    resourceType?: string;
    /**
     * Identifier of the affected resource, when applicable. Sanctioned PII
     * exception: for resourceType "sms_consent"/"voice_consent" this holds the
     * phone number, because consent events are legally meaningless without it.
     * That is the ONLY PII allowed anywhere in an audit record.
     */
    resourceId?: string;
    /**
     * Small, non-PII contextual data only (e.g. { source, eventType }). Never
     * store field values, diffs, or PII (email, address, license, financials).
     */
    metadata?: Record<string, unknown>;
    createdAt?: Date;
    updatedAt?: Date;
}

/**
 * A normalized audit-history entry as returned by GET /v1/audit-log — the shape
 * the admin timeline UI consumes. Sources beyond the audit_log collection (the
 * legacy SQL userLog table) will be normalized into this same shape and merged
 * server-side, so `action`/`category` are plain strings: legacy rows won't map
 * onto EAuditAction/EAuditCategory.
 */
export interface IAuditHistoryEntry {
    /** ISO timestamp of when the action occurred. */
    timestamp: string;
    /** Which system recorded the entry. */
    source: "audit" | "legacy";
    /** EAuditAction values for "audit" entries; free-text for legacy rows. */
    action: string;
    /** EAuditCategory values for "audit" entries; absent for legacy rows. */
    category?: string;
    /** User who performed the action (omitted for system/unauthenticated events). */
    actorUserId?: string;
    /** Type of resource affected, e.g. "client", "report", "branding". */
    resourceType?: string;
    /** Identifier of the affected resource, when applicable. */
    resourceId?: string;
    /** Small, non-PII contextual data only. */
    metadata?: Record<string, unknown>;
}
